import { useSession } from 'next-auth/react';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';

/**
 * @description Simple hook to check if the user is authenticated using NextAuth session
 * **isAuth**: boolean - check if user has a valid NextAuth session
 * **isSafeAuth**: boolean - safe auth state to prevent hydration mismatches
 * **redirectToLogin**: function - redirect to login page if not authenticated
 */
export const useAuthClient = () => {
  const { router } = useExtendedRouter();
  const { data: session, status } = useSession();

  // Check if the user is authenticated based on NextAuth session
  const isAuth = status === 'authenticated' && !!session;

  // Safe auth state - same as isAuth but can be used to prevent hydration mismatches
  const isSafeAuth = isAuth;

  // Redirect to login page if not authenticated
  // Uses useExtendedRouter which automatically adds locale prefix (e.g., /de/login for DE users)
  const redirectToLogin = (afterLoginRedirectPath?: string) => {
    if (!isAuth) {
      let loginUrl = '/login';
      if (afterLoginRedirectPath) {
        loginUrl = `/login?targetPath=${encodeURIComponent(afterLoginRedirectPath)}`;
      }
      router.push(loginUrl);
    }
  };

  return {
    isAuth,
    isSafeAuth,
    checkAuthCookie: () => isAuth, // Legacy compatibility
    redirectToLogin,
  };
};
